import { useState } from 'react'
import { Download, X, FileText, FileJson, Calendar } from 'lucide-react'
import UserList from '@/components/user/UserList'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

const EXPORT_FIELDS = [
  { key: 'user_id', label: 'ID utilisateur' },
  { key: 'email', label: 'Courriel' },
  { key: 'first_name', label: 'Prénom' },
  { key: 'last_name', label: 'Nom' },
  { key: 'role', label: 'Rôle' },
  { key: 'is_active', label: 'Actif' },
  { key: 'is_verified', label: 'Vérifié' },
  { key: 'created_at', label: 'Créé le' },
  { key: 'last_login', label: 'Dernière connexion' },
]

const UsersPage = () => {
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv')
  const [selectedFields, setSelectedFields] = useState<string[]>(EXPORT_FIELDS.map(f => f.key))
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [exportLoading, setExportLoading] = useState(false)

  const toggleField = (key: string) => {
    setSelectedFields(prev =>
      prev.includes(key)
        ? prev.filter(f => f !== key)
        : [...prev, key]
    )
  }

  const handleExport = async () => {
    try {
      setExportLoading(true)
      const { userService } = await import('@/services/userService')
      
      const blob = await userService.exportUsers(exportFormat, {
        fields: selectedFields,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      })
      
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.${exportFormat}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setShowExportModal(false)
    } catch (error: any) {
      alert(`Échec de l'exportation : ${error.message}`)
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <UserList />

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg mx-4">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Download className="h-5 w-5 mr-2 text-primary-600" />
                  Export Users
                </h3>
                <Button variant="ghost" size="icon" onClick={() => setShowExportModal(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Format Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Export Format</label>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setExportFormat('csv')}
                    className={`flex-1 flex items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                      exportFormat === 'csv'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <FileText className="h-5 w-5 mr-2" />
                    <span className="font-medium">CSV</span>
                  </button>
                  <button
                    onClick={() => setExportFormat('json')}
                    className={`flex-1 flex items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                      exportFormat === 'json'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <FileJson className="h-5 w-5 mr-2" />
                    <span className="font-medium">JSON</span>
                  </button>
                </div>
              </div>

              {/* Field Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fields to Export
                  <span className="text-gray-400 ml-1">({selectedFields.length} selected)</span>
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                  {EXPORT_FIELDS.map(field => (
                    <label
                      key={field.key}
                      className={`flex items-center p-2 rounded cursor-pointer transition-colors ${
                        selectedFields.includes(field.key)
                          ? 'bg-primary-50 text-primary-700'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(field.key)}
                        onChange={() => toggleField(field.key)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                      />
                      <span className="text-sm">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Date Range */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Range <span className="text-gray-400">(optional)</span>
                </label>
                <div className="flex space-x-3">
                  <div className="flex-1">
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="ghost" onClick={() => setShowExportModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleExport}
                  disabled={exportLoading || selectedFields.length === 0}
                >
                  {exportLoading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Export {exportFormat.toUpperCase()}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default UsersPage
